import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { 
  Search, 
  Plus, 
  Download, 
  Mail, 
  QrCode, 
  Users, 
  CreditCard,
  Eye,
  Trash2,
  Filter,
  RefreshCw
} from 'lucide-react';
import { digitalCardsApi } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { membersApi } from '@/lib/api';

interface DigitalCard {
  id: number;
  card_number: string;
  member: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    membership_type: string;
    status: string;
  };
  qr_code: string;
  issued_date: string;
  expiry_date: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Statistics {
  total_cards: number;
  active_cards: number;
  expired_cards: number;
  issued_this_month: number;
}

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  membership_type: string;
  status: string;
  has_digital_card: boolean;
}

const AdminDigitalCards: React.FC = () => {
  const [cards, setCards] = useState<DigitalCard[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isValidateQROpen, setIsValidateQROpen] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [emailData, setEmailData] = useState({ email: '', message: '' });
  const [qrData, setQrData] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(15);
  const [validationResult, setValidationResult] = useState<any>(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchTerm]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch statistics, cards, and members in parallel
      const [statsResponse, cardsResponse, membersResponse] = await Promise.all([
        digitalCardsApi.getStatistics(),
        digitalCardsApi.getAll({ 
          search: searchTerm || undefined, 
          per_page: perPage 
        }),
        membersApi.getAll({ status: 'active' }) // Only active members for card generation
      ]);
      
      setStatistics(statsResponse as Statistics);
      const cardsData = (cardsResponse as any)?.data || cardsResponse;
      setCards(cardsData);
      
      // Process members to show who has/doesn't have cards
      const membersList = Array.isArray(membersResponse) ? membersResponse : (membersResponse as any)?.data || [];
      const cardsMap = new Set(cardsData.map((card: DigitalCard) => card.member.id));
      const processedMembers = membersList.map((member: any) => ({
        ...member,
        has_digital_card: cardsMap.has(member.id)
      }));
      setMembers(processedMembers);
      
    } catch (error: any) {
      console.error('Error fetching digital cards data:', error);
      toast({
        title: "Error",
        description: "Failed to load digital cards data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateCard = async (memberId: number) => {
    try {
      await digitalCardsApi.generate(memberId);
      toast({
        title: "Success",
        description: "Digital card generated successfully",
      });
      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate digital card",
        variant: "destructive",
      });
    }
  };

  const handleBulkGenerate = async () => {
    if (selectedMemberIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one member",
        variant: "destructive",
      });
      return;
    }

    try {
      await digitalCardsApi.generateBulk(selectedMemberIds);
      toast({
        title: "Success",
        description: `Generated ${selectedMemberIds.length} digital cards successfully`,
      });
      setIsGenerateDialogOpen(false);
      setSelectedMemberIds([]);
      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate digital cards",
        variant: "destructive",
      });
    }
  };

  const handleDownloadCard = async (cardId: number) => {
    try {
      const blob = await digitalCardsApi.downloadSingle(cardId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `digital-card-${cardId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "Digital card downloaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to download digital card",
        variant: "destructive",
      });
    }
  };

  const handleBulkDownload = async () => {
    if (selectedCards.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one card",
        variant: "destructive",
      });
      return;
    }

    try {
      const blob = await digitalCardsApi.downloadBulk(selectedCards);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `digital-cards-bulk.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: `Downloaded ${selectedCards.length} digital cards successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to download digital cards",
        variant: "destructive",
      });
    }
  };

  const handleEmailCard = async (cardId: number) => {
    try {
      await digitalCardsApi.email(cardId, emailData.email, emailData.message);
      toast({
        title: "Success",
        description: "Digital card emailed successfully",
      });
      setIsEmailDialogOpen(false);
      setEmailData({ email: '', message: '' });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to email digital card",
        variant: "destructive",
      });
    }
  };

  const handleValidateQR = async () => {
    if (!qrData.trim()) {
      toast({
        title: "Error",
        description: "Please enter QR code data",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await digitalCardsApi.validateQR(qrData);
      setValidationResult(result);
      toast({
        title: "QR Code Validated",
        description: "QR code is valid and verified",
      });
    } catch (error: any) {
      setValidationResult(null);
      toast({
        title: "Invalid QR Code",
        description: error.response?.data?.message || "QR code validation failed",
        variant: "destructive",
      });
    }
  };

  const handleViewPremiumCard = (cardId: number) => {
    // Open premium card in new window/tab
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "https://4b08-197-186-9-241.ngrok-free.app/api";
    window.open(`${baseUrl}/digital-cards/premium/${cardId}`, '_blank');
  };

  const handleCardSelection = (cardId: number, checked: boolean) => {
    setSelectedCards(prev => 
      checked 
        ? [...prev, cardId]
        : prev.filter(id => id !== cardId)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedCards(checked ? cards.map(card => card.id) : []);
  };

  const handleMemberSelection = (memberId: number, checked: boolean) => {
    if (checked) {
      setSelectedMemberIds([...selectedMemberIds, memberId]);
    } else {
      setSelectedMemberIds(selectedMemberIds.filter(id => id !== memberId));
    }
  };

  const handleSelectAllMembers = (checked: boolean) => {
    if (checked) {
      const membersWithoutCards = members.filter(m => !m.has_digital_card).map(m => m.id);
      setSelectedMemberIds(membersWithoutCards);
    } else {
      setSelectedMemberIds([]);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { variant: 'default', label: 'Active' },
      expired: { variant: 'destructive', label: 'Expired' },
      suspended: { variant: 'secondary', label: 'Suspended' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || 
                  { variant: 'outline', label: status };
    
    return (
      <Badge variant={config.variant as any}>
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Digital Cards Management</h1>
          <p className="text-gray-600">Manage digital membership cards for all members</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Cards</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statistics.total_cards}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Cards</CardTitle>
                <Users className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{statistics.active_cards}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Expired Cards</CardTitle>
                <Users className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{statistics.expired_cards}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Issued This Month</CardTitle>
                <Plus className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{statistics.issued_this_month}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search cards by member name, email, or card number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Dialog open={isGenerateDialogOpen} onOpenChange={setIsGenerateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Cards
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Generate Digital Cards</DialogTitle>
                  <DialogDescription>
                    Select members to generate digital cards for
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="max-h-64 overflow-y-auto border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Checkbox
                        checked={selectedMemberIds.length === members.filter(m => !m.has_digital_card).length && members.filter(m => !m.has_digital_card).length > 0}
                        onCheckedChange={handleSelectAllMembers}
                      />
                      <Label className="text-sm font-medium">Select All Members Without Cards</Label>
                    </div>
                    
                    {members.filter(m => !m.has_digital_card).map((member) => (
                      <div key={member.id} className="flex items-center space-x-2 py-2">
                        <Checkbox
                          checked={selectedMemberIds.includes(member.id)}
                          onCheckedChange={(checked) => handleMemberSelection(member.id, checked as boolean)}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {member.email} • {member.membership_type}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {members.filter(m => !m.has_digital_card).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        All active members already have digital cards
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsGenerateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleBulkGenerate}
                      disabled={selectedMemberIds.length === 0}
                    >
                      Generate {selectedMemberIds.length} Cards
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button 
              variant="outline" 
              onClick={handleBulkDownload}
              disabled={selectedCards.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Selected ({selectedCards.length})
            </Button>

            <Dialog open={isValidateQROpen} onOpenChange={setIsValidateQROpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <QrCode className="w-4 h-4 mr-2" />
                  Validate QR
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Validate QR Code</DialogTitle>
                  <DialogDescription>
                    Enter QR code data to validate membership card
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Textarea
                    placeholder="Paste QR code data here..."
                    value={qrData}
                    onChange={(e) => setQrData(e.target.value)}
                    rows={4}
                  />
                  
                  {validationResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-medium text-green-900 mb-2">Valid Card Found</h4>
                      <div className="text-sm text-green-800">
                        <p><strong>Member:</strong> {validationResult.member_name}</p>
                        <p><strong>Card Number:</strong> {validationResult.card_number}</p>
                        <p><strong>Status:</strong> {validationResult.status}</p>
                        <p><strong>Expiry:</strong> {new Date(validationResult.expiry_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                      setIsValidateQROpen(false);
                      setValidationResult(null);
                      setQrData('');
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleValidateQR}>
                      Validate
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Cards Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Digital Cards</CardTitle>
                <CardDescription>
                  {cards.length} cards found
                </CardDescription>
              </div>
              
              {selectedCards.length > 0 && (
                <div className="text-sm text-gray-600">
                  {selectedCards.length} selected
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">
                      <Checkbox
                        checked={selectedCards.length === cards.length && cards.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left p-2">Card Number</th>
                    <th className="text-left p-2">Member</th>
                    <th className="text-left p-2">Membership Type</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Issued Date</th>
                    <th className="text-left p-2">Expiry Date</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card) => (
                    <tr key={card.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <Checkbox
                          checked={selectedCards.includes(card.id)}
                          onCheckedChange={(checked) => handleCardSelection(card.id, checked as boolean)}
                        />
                      </td>
                      <td className="p-2 font-mono text-sm">{card.card_number}</td>
                      <td className="p-2">
                        <div>
                          <div className="font-medium">
                            {card.member.first_name} {card.member.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{card.member.email}</div>
                        </div>
                      </td>
                      <td className="p-2">{card.member.membership_type}</td>
                      <td className="p-2">{getStatusBadge(card.status)}</td>
                      <td className="p-2">{new Date(card.issued_date).toLocaleDateString()}</td>
                      <td className="p-2">{new Date(card.expiry_date).toLocaleDateString()}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDownloadCard(card.id)}
                            title="Download PDF"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="outline" title="Email Card">
                                <Mail className="w-3 h-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Email Digital Card</DialogTitle>
                                <DialogDescription>
                                  Send digital card to member or custom email
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <Input
                                  placeholder="Email address"
                                  value={emailData.email || card.member.email}
                                  onChange={(e) => setEmailData(prev => ({ ...prev, email: e.target.value }))}
                                />
                                <Textarea
                                  placeholder="Optional message..."
                                  value={emailData.message}
                                  onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                                  rows={3}
                                />
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline">Cancel</Button>
                                  <Button onClick={() => handleEmailCard(card.id)}>
                                    Send Email
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewPremiumCard(card.id)}
                            title="View Premium Card"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {cards.length === 0 && !isLoading && (
                <div className="text-center py-8 text-gray-500">
                  No digital cards found
                </div>
              )}
              
              {isLoading && (
                <div className="text-center py-8">
                  <LoadingSpinner />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDigitalCards; 